import { NurseLayout } from "@/components/layout/nurse-layout";
import { useParams, Link } from "wouter";
import { useState, useEffect, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Upload, Trash2, Download, Loader2, CheckCircle, Baby, Save, FileText, ShieldCheck, X, Mail, Clock, AlertTriangle } from "lucide-react";
import { VoiceInputButton } from "@/components/ui/voice-input-button";
import { appendVoiceChunk } from "@/lib/voice";

type PracticeLevel = "specialist" | "advanced" | "consultant";
type PortfolioStatus = "not_started" | "in_progress" | "achieved";

const LEVEL_KEY = "rheumhub_practice_level";

interface Statement {
  id: number;
  code: string;
  statement: string;
  specialistLevel: string;
  advancedLevel: string;
  consultantLevel: string;
  isPaediatric: boolean;
}

interface PortfolioEntry {
  id: number;
  status: PortfolioStatus;
  practiceLevel: PracticeLevel;
  reflectionText: string | null;
  assessorNotes: string | null;
  achievedAt: string | null;
  signedOffByName: string | null;
  signedOffByRole: string | null;
  signedOffByEmail: string | null;
  signedOffAt: string | null;
  signOffNotes: string | null;
}

interface EvidenceFile {
  id: number;
  fileName: string;
  fileKey: string;
  fileSize: number;
  mimeType: string;
  uploadedAt: string;
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function CompetencyEntry() {
  const params = useParams<{ id: string; statementId: string }>();
  const domainId = params.id;
  const statementId = params.statementId;

  const [statement, setStatement] = useState<Statement | null>(null);
  const [entry, setEntry] = useState<PortfolioEntry | null>(null);
  const [evidence, setEvidence] = useState<EvidenceFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [level, setLevel] = useState<PracticeLevel>(
    () => (localStorage.getItem(LEVEL_KEY) as PracticeLevel) ?? "specialist"
  );
  const [status, setStatus] = useState<PortfolioStatus>("not_started");
  const [reflection, setReflection] = useState("");
  const [assessorNotes, setAssessorNotes] = useState("");
  const [achievedAt, setAchievedAt] = useState("");
  const [viewLevel, setViewLevel] = useState<PracticeLevel>(level);

  const [signOffOpen, setSignOffOpen] = useState(false);
  const [signOffName, setSignOffName] = useState("");
  const [signOffRole, setSignOffRole] = useState("");
  const [signOffEmail, setSignOffEmail] = useState("");
  const [signOffNotes, setSignOffNotes] = useState("");
  const [signOffSaving, setSignOffSaving] = useState(false);
  const [signOffError, setSignOffError] = useState<string | null>(null);

  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [reqName, setReqName] = useState("");
  const [reqRole, setReqRole] = useState("");
  const [reqEmail, setReqEmail] = useState("");
  const [reqSending, setReqSending] = useState(false);
  const [reqError, setReqError] = useState<string | null>(null);
  const [reqSent, setReqSent] = useState(false);
  const [pendingRequests, setPendingRequests] = useState<Array<{
    id: number;
    supervisorName: string;
    supervisorRole: string;
    supervisorEmail: string;
    outcome: string;
    expiresAt: string;
    createdAt: string;
  }>>([]);

  const fileRef = useRef<HTMLInputElement>(null);
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/competency/portfolio/${statementId}`);
        if (res.ok) {
          const data = await res.json();
          setStatement(data.statement);
          if (data.entry) {
            setEntry(data.entry);
            setStatus(data.entry.status);
            setLevel(data.entry.practiceLevel);
            setViewLevel(data.entry.practiceLevel);
            setReflection(data.entry.reflectionText ?? "");
            setAssessorNotes(data.entry.assessorNotes ?? "");
            setAchievedAt(data.entry.achievedAt ? data.entry.achievedAt.split("T")[0] : "");
            setSignOffName(data.entry.signedOffByName ?? "");
            setSignOffRole(data.entry.signedOffByRole ?? "");
            setSignOffEmail(data.entry.signedOffByEmail ?? "");
            setSignOffNotes(data.entry.signOffNotes ?? "");

            const reqRes = await fetch(`/api/competency/portfolio/${statementId}/sign-off/requests`);
            if (reqRes.ok) {
              const reqs = await reqRes.json();
              setPendingRequests(reqs);
            }
          }
          setEvidence(data.evidence ?? []);
        }
      } finally {
        setLoading(false);
        isInitialLoad.current = false;
      }
    };
    load();
  }, [statementId]);

  const doSave = useCallback(async (
    lvl: PracticeLevel,
    st: PortfolioStatus,
    refl: string,
    notes: string,
    date: string,
  ) => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const res = await fetch(`/api/competency/portfolio/${statementId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          practiceLevel: lvl,
          status: st,
          reflectionText: refl,
          assessorNotes: notes,
          ...(date ? { achievedAt: date } : {}),
        }),
      });
      if (!res.ok) throw new Error("Save failed");
      const updated = await res.json();
      setEntry(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }, [statementId]);

  const scheduleAutosave = useCallback((
    lvl: PracticeLevel,
    st: PortfolioStatus,
    refl: string,
    notes: string,
    date: string,
  ) => {
    if (isInitialLoad.current) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => doSave(lvl, st, refl, notes, date), 2000);
  }, [doSave]);

  useEffect(() => {
    scheduleAutosave(level, status, reflection, assessorNotes, achievedAt);
    return () => {
      if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    };
  }, [level, status, reflection, assessorNotes, achievedAt]);

  const handleSave = () => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    doSave(level, status, reflection, assessorNotes, achievedAt);
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const MAX_SIZE = 10 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      setError("File must be under 10 MB");
      return;
    }

    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const evRes = await fetch(`/api/competency/portfolio/${statementId}/evidence`, {
        method: "POST",
        body: form,
      });
      if (!evRes.ok) throw new Error("Upload failed");
      const ev = await evRes.json();
      setEvidence((prev) => [...prev, ev]);
    } catch {
      setError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleSignOffSave = async () => {
    setSignOffError(null);
    if (!signOffName.trim() || !signOffRole.trim()) {
      setSignOffError("Supervisor name and role are required.");
      return;
    }
    if (!entry) {
      setSignOffError("Save your portfolio entry first, then record sign-off.");
      return;
    }
    setSignOffSaving(true);
    try {
      const res = await fetch(`/api/competency/portfolio/${statementId}/sign-off`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          signedOffByName: signOffName.trim(),
          signedOffByRole: signOffRole.trim(),
          signedOffByEmail: signOffEmail.trim(),
          signOffNotes: signOffNotes.trim(),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? "Sign-off failed");
      }
      const updated = await res.json();
      setEntry(updated);
      setSignOffOpen(false);
    } catch (err) {
      setSignOffError(err instanceof Error ? err.message : "Sign-off failed");
    } finally {
      setSignOffSaving(false);
    }
  };

  const handleSignOffClear = async () => {
    if (!entry?.signedOffAt) return;
    if (!window.confirm("Clear this supervisor sign-off? You'll need to record a new one to confirm achievement.")) return;
    setSignOffSaving(true);
    setSignOffError(null);
    try {
      const res = await fetch(`/api/competency/portfolio/${statementId}/sign-off`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to clear sign-off");
      const updated = await res.json();
      setEntry(updated);
      setSignOffName("");
      setSignOffRole("");
      setSignOffEmail("");
      setSignOffNotes("");
    } catch (err) {
      setSignOffError(err instanceof Error ? err.message : "Failed to clear sign-off");
    } finally {
      setSignOffSaving(false);
    }
  };

  const handleSendSignOffRequest = async () => {
    setReqError(null);
    if (!reqName.trim() || !reqRole.trim() || !reqEmail.trim()) {
      setReqError("Supervisor name, role, and email are all required.");
      return;
    }
    if (!entry) {
      setReqError("Save your portfolio entry first before sending a sign-off request.");
      return;
    }
    setReqSending(true);
    try {
      const res = await fetch(`/api/competency/portfolio/${statementId}/sign-off/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          supervisorName: reqName.trim(),
          supervisorRole: reqRole.trim(),
          supervisorEmail: reqEmail.trim(),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? "Failed to send request");
      }
      const newReq = await res.json();
      setPendingRequests((prev) => [{ ...newReq, supervisorName: reqName.trim(), supervisorRole: reqRole.trim(), supervisorEmail: reqEmail.trim(), outcome: "pending", createdAt: new Date().toISOString() }, ...prev]);
      setReqSent(true);
    } catch (err) {
      setReqError(err instanceof Error ? err.message : "Failed to send request");
    } finally {
      setReqSending(false);
    }
  };

  const handleDeleteEvidence = async (evId: number, fileName: string) => {
    if (!window.confirm(`Delete "${fileName}"? This cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/competency/portfolio/${statementId}/evidence/${evId}`, {
        method: "DELETE",
      });
      if (res.ok) setEvidence((prev) => prev.filter((e) => e.id !== evId));
      else setError("Delete failed");
    } catch {
      setError("Delete failed");
    }
  };

  const levelText = (l: PracticeLevel) => {
    if (!statement) return "";
    if (l === "specialist") return statement.specialistLevel;
    if (l === "advanced") return statement.advancedLevel;
    return statement.consultantLevel;
  };

  if (loading) {
    return <NurseLayout title="Loading..."><div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-teal-600" /></div></NurseLayout>;
  }

  if (!statement) {
    return <NurseLayout title="Not Found"><p className="text-slate-500">Competency statement not found.</p></NurseLayout>;
  }

  return (
    <NurseLayout title={statement.code}>
      <div className="max-w-3xl space-y-6">
        <div className="flex items-center gap-3">
          <Link href={`/nurse/competency/${domainId}`}>
            <Button variant="ghost" size="sm" className="gap-1 text-slate-500">
              <ArrowLeft className="h-4 w-4" />
              Back to Domain
            </Button>
          </Link>
        </div>

        {/* Statement Display */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-slate-400">{statement.code}</span>
            {statement.isPaediatric && (
              <span className="flex items-center gap-1 text-xs text-purple-600 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-full">
                <Baby className="h-3 w-3" />
                Paediatric
              </span>
            )}
          </div>
          <h2 className="text-lg font-bold text-slate-900">{statement.statement}</h2>

          {/* Level tabs */}
          <div className="border-b border-slate-200">
            <div className="flex gap-0">
              {(["specialist", "advanced", "consultant"] as PracticeLevel[]).map((l) => (
                <button
                  key={l}
                  onClick={() => setViewLevel(l)}
                  className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors capitalize ${
                    viewLevel === l
                      ? "border-teal-600 text-teal-700"
                      : "border-transparent text-slate-500 hover:text-slate-800"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">{levelText(viewLevel)}</p>
        </div>

        {/* Portfolio Entry Form */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-5">
          <h3 className="font-bold text-slate-900">Your Portfolio Entry</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="comp-level" className="block text-xs font-medium text-slate-600 mb-1.5">Practice Level</label>
              <select
                id="comp-level"
                value={level}
                onChange={(e) => setLevel(e.target.value as PracticeLevel)}
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="specialist">Specialist Practitioner</option>
                <option value="advanced">Advanced Practitioner</option>
                <option value="consultant">Consultant Practitioner</option>
              </select>
            </div>
            <div>
              <label htmlFor="comp-status" className="block text-xs font-medium text-slate-600 mb-1.5">Status</label>
              <select
                id="comp-status"
                value={status}
                onChange={(e) => setStatus(e.target.value as PortfolioStatus)}
                className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="not_started">Not Started</option>
                <option value="in_progress">In Progress</option>
                <option value="achieved">Achieved</option>
              </select>
            </div>
          </div>

          {status === "achieved" && (
            <div>
              <label htmlFor="comp-achieved" className="block text-xs font-medium text-slate-600 mb-1.5">Date Achieved</label>
              <input
                id="comp-achieved"
                type="date"
                value={achievedAt}
                onChange={(e) => setAchievedAt(e.target.value)}
                className="h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="comp-reflection" className="block text-xs font-medium text-slate-600">
                Reflection <span className="text-slate-400 font-normal">(Describe how you meet this competency)</span>
              </label>
              <VoiceInputButton
                tone="teal"
                size="xs"
                onTranscript={(chunk) => setReflection((prev) => appendVoiceChunk(prev, chunk))}
                label="Dictate reflection"
              />
            </div>
            <textarea
              id="comp-reflection"
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              rows={5}
              placeholder="Reflect on how your practice demonstrates this competency. Consider specific examples, patients, or situations where you have applied these skills..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label htmlFor="comp-assessor" className="block text-xs font-medium text-slate-600">Assessor Notes</label>
              <VoiceInputButton
                tone="teal"
                size="xs"
                onTranscript={(chunk) => setAssessorNotes((prev) => appendVoiceChunk(prev, chunk))}
                label="Dictate assessor notes"
              />
            </div>
            <textarea
              id="comp-assessor"
              value={assessorNotes}
              onChange={(e) => setAssessorNotes(e.target.value)}
              rows={3}
              placeholder="Notes from line manager, mentor, or peer assessor..."
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
            />
          </div>

          {error && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">{error}</p>}

          <div className="flex items-center gap-3">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-teal-700 hover:bg-teal-800 text-white gap-2"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? "Saving..." : "Save Entry"}
            </Button>
            {saved && (
              <span className="flex items-center gap-1.5 text-sm text-green-600">
                <CheckCircle className="h-4 w-4" />
                Saved
              </span>
            )}
          </div>
        </div>

        {/* Supervisor Sign-Off Panel */}
        <div className={`rounded-2xl p-6 border shadow-sm space-y-4 ${
          entry?.signedOffAt ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200"
        }`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <ShieldCheck className={`h-5 w-5 mt-0.5 shrink-0 ${entry?.signedOffAt ? "text-emerald-600" : "text-slate-400"}`} />
              <div>
                <h3 className="font-bold text-slate-900">Supervisor Sign-Off</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Request sign-off by email or record validation already received in person.
                </p>
              </div>
            </div>
            {entry?.signedOffAt && !signOffOpen && (
              <Button
                onClick={handleSignOffClear}
                variant="ghost"
                size="sm"
                disabled={signOffSaving}
                className="gap-1 text-slate-500 hover:text-red-600"
              >
                <X className="h-3.5 w-3.5" />
                Clear
              </Button>
            )}
          </div>

          {entry?.signedOffAt && !signOffOpen ? (
            <div className="bg-white rounded-xl p-4 border border-emerald-200 space-y-2">
              <div className="flex items-center gap-2 text-emerald-700">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm font-semibold">
                  Signed off on {new Date(entry.signedOffAt).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                </span>
              </div>
              <div className="text-sm text-slate-800">
                <span className="font-medium">{entry.signedOffByName}</span>
                {entry.signedOffByRole && <span className="text-slate-500"> · {entry.signedOffByRole}</span>}
              </div>
              {entry.signedOffByEmail && (
                <div className="text-xs text-slate-500">{entry.signedOffByEmail}</div>
              )}
              {entry.signOffNotes && (
                <p className="text-xs text-slate-600 leading-relaxed mt-2 pt-2 border-t border-emerald-100 whitespace-pre-wrap">{entry.signOffNotes}</p>
              )}
              <div className="pt-2">
                <Button
                  onClick={() => setSignOffOpen(true)}
                  variant="outline"
                  size="sm"
                  className="text-xs"
                >
                  Update sign-off details
                </Button>
              </div>
            </div>
          ) : signOffOpen ? (
            <div className="space-y-3 bg-white rounded-xl p-4 border border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="signoff-name" className="block text-xs font-medium text-slate-600 mb-1.5">Supervisor name <span className="text-red-500">*</span></label>
                  <input
                    id="signoff-name"
                    type="text"
                    value={signOffName}
                    onChange={(e) => setSignOffName(e.target.value)}
                    maxLength={200}
                    placeholder="e.g. Jane Smith"
                    className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label htmlFor="signoff-role" className="block text-xs font-medium text-slate-600 mb-1.5">Role / Job title <span className="text-red-500">*</span></label>
                  <input
                    id="signoff-role"
                    type="text"
                    value={signOffRole}
                    onChange={(e) => setSignOffRole(e.target.value)}
                    maxLength={200}
                    placeholder="e.g. Lead Rheumatology Nurse"
                    className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="signoff-email" className="block text-xs font-medium text-slate-600 mb-1.5">Supervisor email <span className="text-slate-400 font-normal">(optional)</span></label>
                <input
                  id="signoff-email"
                  type="email"
                  value={signOffEmail}
                  onChange={(e) => setSignOffEmail(e.target.value)}
                  maxLength={320}
                  placeholder="jane.smith@nhs.net"
                  className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              <div>
                <label htmlFor="signoff-notes" className="block text-xs font-medium text-slate-600 mb-1.5">Sign-off comments <span className="text-slate-400 font-normal">(optional)</span></label>
                <textarea
                  id="signoff-notes"
                  value={signOffNotes}
                  onChange={(e) => setSignOffNotes(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="e.g. Observed in clinic on 12 March; competency demonstrated to advanced level."
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                />
              </div>
              <p className="text-xs text-slate-500">
                Recording sign-off here is a contemporaneous record of validation already received from your supervisor (e.g. by email, signed form, or in-person observation). The timestamp is set to now and the action is logged against your account.
              </p>
              {signOffError && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">{signOffError}</p>}
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleSignOffSave}
                  disabled={signOffSaving || !entry}
                  className="bg-emerald-700 hover:bg-emerald-800 text-white gap-2"
                  size="sm"
                >
                  {signOffSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                  {signOffSaving ? "Recording..." : entry?.signedOffAt ? "Update Sign-Off" : "Record Sign-Off"}
                </Button>
                <Button
                  onClick={() => {
                    setSignOffOpen(false);
                    setSignOffError(null);
                    setSignOffName(entry?.signedOffByName ?? "");
                    setSignOffRole(entry?.signedOffByRole ?? "");
                    setSignOffEmail(entry?.signedOffByEmail ?? "");
                    setSignOffNotes(entry?.signOffNotes ?? "");
                  }}
                  variant="ghost"
                  size="sm"
                  disabled={signOffSaving}
                >
                  Cancel
                </Button>
              </div>
              {!entry && (
                <p className="text-xs text-amber-600">Save your portfolio entry above first, then you can record a sign-off.</p>
              )}
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={() => setSignOffOpen(true)}
                variant="outline"
                size="sm"
                disabled={!entry}
                className="gap-2"
              >
                <ShieldCheck className="h-4 w-4" />
                Record sign-off manually
              </Button>
              <Button
                onClick={() => { setRequestModalOpen(true); setReqSent(false); setReqError(null); }}
                variant="outline"
                size="sm"
                disabled={!entry}
                className="gap-2 border-teal-200 text-teal-700 hover:bg-teal-50"
              >
                <Mail className="h-4 w-4" />
                Request sign-off by email
              </Button>
            </div>
          )}

          {/* Recent sign-off requests */}
          {pendingRequests.length > 0 && !entry?.signedOffAt && (
            <div className="space-y-2 pt-2 border-t border-slate-100">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Recent sign-off requests</p>
              {pendingRequests.map((r) => {
                const isExpired = new Date(r.expiresAt) < new Date();
                const outcomeColors: Record<string, string> = {
                  pending: "text-amber-600",
                  confirmed: "text-emerald-600",
                  declined: "text-red-500",
                };
                const outcomeIcons: Record<string, React.ReactNode> = {
                  pending: <Clock className="h-3.5 w-3.5" />,
                  confirmed: <CheckCircle className="h-3.5 w-3.5" />,
                  declined: <X className="h-3.5 w-3.5" />,
                };
                return (
                  <div key={r.id} className="flex items-start gap-3 bg-slate-50 rounded-xl p-3">
                    <span className={`mt-0.5 ${outcomeColors[r.outcome] ?? "text-slate-400"}`}>
                      {outcomeIcons[r.outcome] ?? <AlertTriangle className="h-3.5 w-3.5" />}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-800">{r.supervisorName} · {r.supervisorRole}</p>
                      <p className="text-xs text-slate-400">{r.supervisorEmail}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        Sent {new Date(r.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                        {" "}·{" "}
                        <span className={`font-medium ${outcomeColors[r.outcome] ?? ""}`}>
                          {isExpired && r.outcome === "pending" ? "Expired" : r.outcome.charAt(0).toUpperCase() + r.outcome.slice(1)}
                        </span>
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Request sign-off by email modal */}
        {requestModalOpen && (
          <div role="presentation" className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4" onClick={(e) => { if (e.target === e.currentTarget) { setRequestModalOpen(false); setReqSent(false); } }} onKeyDown={(e) => { if ((e.key === "Escape") && e.target === e.currentTarget) { setRequestModalOpen(false); setReqSent(false); } }}>
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-md p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-teal-600" />
                  <h2 className="text-base font-bold text-slate-900">Request sign-off by email</h2>
                </div>
                <button onClick={() => { setRequestModalOpen(false); setReqSent(false); }} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400">
                  <X className="h-4 w-4" />
                </button>
              </div>

              {reqSent ? (
                <div className="space-y-4">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-4 flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-emerald-800">Request sent</p>
                      <p className="text-xs text-emerald-700 mt-0.5 leading-relaxed">
                        An email has been sent to <strong>{reqEmail}</strong> with a secure link to review your reflection and confirm sign-off. The link expires in 7 days.
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => { setRequestModalOpen(false); setReqSent(false); }} className="w-full">Done</Button>
                </div>
              ) : (
                <>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    Enter your supervisor's details. They will receive an email with a secure, single-use link to review your reflection and confirm or decline sign-off.
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label htmlFor="signoff-supervisor-name" className="block text-xs font-medium text-slate-600 mb-1.5">Supervisor name <span className="text-red-500">*</span></label>
                      <input
                        id="signoff-supervisor-name"
                        type="text"
                        value={reqName}
                        onChange={(e) => setReqName(e.target.value)}
                        maxLength={200}
                        placeholder="e.g. Jane Smith"
                        className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="signoff-supervisor-role" className="block text-xs font-medium text-slate-600 mb-1.5">Role / Job title <span className="text-red-500">*</span></label>
                      <input
                        id="signoff-supervisor-role"
                        type="text"
                        value={reqRole}
                        onChange={(e) => setReqRole(e.target.value)}
                        maxLength={200}
                        placeholder="e.g. Lead Rheumatology Nurse"
                        className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="signoff-supervisor-email" className="block text-xs font-medium text-slate-600 mb-1.5">Supervisor email <span className="text-red-500">*</span></label>
                      <input
                        id="signoff-supervisor-email"
                        type="email"
                        value={reqEmail}
                        onChange={(e) => setReqEmail(e.target.value)}
                        maxLength={320}
                        placeholder="jane.smith@nhs.net"
                        className="w-full h-9 px-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  </div>
                  {reqError && <p className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">{reqError}</p>}
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={handleSendSignOffRequest}
                      disabled={reqSending}
                      className="bg-teal-700 hover:bg-teal-800 text-white gap-2 flex-1"
                    >
                      {reqSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                      {reqSending ? "Sending..." : "Send sign-off request"}
                    </Button>
                    <Button onClick={() => { setRequestModalOpen(false); setReqError(null); }} variant="ghost" size="sm" disabled={reqSending}>Cancel</Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Evidence Panel */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-slate-900">Evidence Files</h3>
            <div>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                className="hidden"
                onChange={handleUpload}
              />
              <Button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                variant="outline"
                className="gap-2"
                size="sm"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? "Uploading..." : "Upload Evidence"}
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-500">PDF, DOCX, JPG, or PNG files up to 10 MB. Upload certificates, observation records, reflections, or other evidence.</p>

          {evidence.length === 0 ? (
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center">
              <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No evidence files uploaded yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {evidence.map((ev) => (
                <div key={ev.id} className="flex items-center gap-3 bg-slate-50 rounded-xl p-3">
                  <FileText className="h-5 w-5 text-slate-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{ev.fileName}</p>
                    <p className="text-xs text-slate-400">{formatBytes(ev.fileSize)} · {new Date(ev.uploadedAt).toLocaleDateString("en-GB")}</p>
                  </div>
                  <a
                    href={`/api/competency/portfolio/${statementId}/evidence/${ev.id}/download`}
                    className="p-1.5 hover:bg-slate-200 rounded-lg transition-colors text-slate-500"
                    title="Download"
                  >
                    <Download className="h-4 w-4" />
                  </a>
                  <button
                    onClick={() => handleDeleteEvidence(ev.id, ev.fileName)}
                    className="p-1.5 hover:bg-red-50 rounded-lg transition-colors text-slate-400 hover:text-red-500"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3">
          Achievement of competencies should be validated by your line manager or a senior peer assessor in line with your trust's appraisal process. This portfolio supports NMC revalidation but does not constitute formal sign-off.
        </div>
      </div>
    </NurseLayout>
  );
}
